// Self-contained printable commission report for an AGENT viewing their
// own dashboard (AgentPortal). Mirrors the admin printable report style
// but uses the agent's own `/agent/me` + `/agent/commissions` payloads
// instead of the admin endpoints.
//
// Why a separate file: the admin report includes payout bank fields and
// admin-only "paid_ref" column. The agent's view should hide those — they
// don't see paid-ref details in their on-screen table either.

const fmtUSD = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(Number(n || 0));

const safe = (s) => (s == null ? "—" : String(s));

const escapeHtml = (s) => safe(s)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

/**
 * @param {object} agent  /agent/me response (full_name, agent_code, commissions roll-up)
 * @param {array}  rows   /agent/commissions rows (filtered to what's on screen)
 * @param {object} opts   { searchQuery: string }
 */
export function buildAgentSelfPrintableReport(agent, rows, opts = {}) {
  const c = agent?.commissions || {};
  const generatedAt = new Date();
  const generatedLine = generatedAt.toLocaleString("en-US", {
    dateStyle: "long", timeStyle: "short",
  });
  const searchQuery = (opts.searchQuery || "").trim();

  const txnTotal = (rows || []).reduce((s, r) => s + (Number(r.transaction_amount) || 0), 0);
  const commTotal = (rows || []).reduce((s, r) => s + (Number(r.commission_amount) || 0), 0);

  const commissionRowsHtml = (rows || []).map((r) => `
    <tr>
      <td class="small">${escapeHtml(new Date(r.earned_at).toLocaleString())}</td>
      <td>
        ${r.receiver_name ? `<div class="strong">${escapeHtml(r.receiver_name)}</div>` : ""}
        ${r.receiver_email ? `<div class="mono small">${escapeHtml(r.receiver_email)}</div>` : ""}
        ${r.receiver_phone ? `<div class="mono small">${escapeHtml(r.receiver_phone)}</div>` : ""}
        ${!(r.receiver_name || r.receiver_email || r.receiver_phone) ? "—" : ""}
      </td>
      <td class="mono small">${escapeHtml(r.customer_email)}</td>
      <td>${escapeHtml(r.transaction_type)}</td>
      <td class="num">${escapeHtml(fmtUSD(r.transaction_amount))}</td>
      <td class="num strong">${escapeHtml(fmtUSD(r.commission_amount))}</td>
      <td>${r.status === "paid" ? '<span class="badge paid">Paid</span>' : '<span class="badge unpaid">Unpaid</span>'}</td>
    </tr>
  `).join("");

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Zcardon · My Commissions · ${escapeHtml(agent?.full_name)} (${escapeHtml(agent?.agent_code)})</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    color: #112318;
    background: #fff;
    margin: 0;
    padding: 32px 40px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .doc { max-width: 1000px; margin: 0 auto; }
  .brand { font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: #4A5C50; }
  h1 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 38px; margin: 4px 0 0; letter-spacing: -0.5px; }
  h2 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 22px; margin: 24px 0 8px; border-bottom: 1px solid #E5E0D8; padding-bottom: 4px; }
  .sub { color: #4A5C50; font-size: 13px; margin-top: 6px; }
  .filter { display: inline-block; padding: 4px 10px; border-radius: 999px; background: #FBF9F6; border: 1px solid #E5E0D8; font-size: 11px; color: #4A5C50; margin-left: 8px; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 16px; }
  .stat { border: 1px solid #E5E0D8; border-radius: 10px; padding: 12px; text-align: center; }
  .stat .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #4A5C50; }
  .stat .val { font-family: "Cormorant Garamond", Georgia, serif; font-size: 22px; margin-top: 6px; }
  .stat.unpaid .val { color: #b45309; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
  th, td { padding: 7px 8px; border-bottom: 1px solid #E5E0D8; text-align: left; vertical-align: top; }
  th { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #4A5C50; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-family: "SF Mono", "Menlo", monospace; }
  td.strong, .strong { font-weight: 600; }
  td.small, .small { font-size: 11px; color: #4A5C50; }
  .mono { font-family: "SF Mono", "Menlo", monospace; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600; }
  .badge.paid   { background: #DCF7E8; color: #166434; }
  .badge.unpaid { background: #FEF3C7; color: #92400E; }
  tfoot td { border-top: 2px solid #184A30; border-bottom: none; padding-top: 10px; font-weight: 700; background: #FBF9F6; }
  tfoot td.label { font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: #4A5C50; font-weight: 600; }
  tfoot td.grand { font-size: 16px; color: #184A30; }
  .footer { margin-top: 32px; font-size: 10px; color: #4A5C50; text-align: center; }
  .actions { position: fixed; top: 12px; right: 12px; }
  .actions button { padding: 6px 14px; border-radius: 999px; border: 1px solid #112318; background: #112318; color: #fff; font-size: 12px; cursor: pointer; margin-left: 6px; }
  .actions button.secondary { background: #fff; color: #112318; }
  @media print { .actions { display: none; } body { padding: 0 16px; } }
</style>
</head>
<body>
  <div class="actions">
    <button onclick="window.print()">Print</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="doc">
    <div class="brand">Zcardon · My commission report</div>
    <h1>${escapeHtml(agent?.full_name)}</h1>
    <div class="sub">
      Agent code <span class="mono strong">${escapeHtml(agent?.agent_code)}</span>
      · Commission rate <span class="strong">${escapeHtml(agent?.commission_percent ?? 0.5)}%</span>
      ${searchQuery ? `<span class="filter">Filter: "${escapeHtml(searchQuery)}"</span>` : ""}
    </div>
    <div class="sub">Generated ${escapeHtml(generatedLine)}</div>

    <div class="stats">
      <div class="stat"><div class="label">This week</div><div class="val">${escapeHtml(fmtUSD(c.week?.total ?? 0))}</div></div>
      <div class="stat"><div class="label">This month</div><div class="val">${escapeHtml(fmtUSD(c.month?.total ?? 0))}</div></div>
      <div class="stat"><div class="label">This year</div><div class="val">${escapeHtml(fmtUSD(c.year?.total ?? 0))}</div></div>
      <div class="stat unpaid"><div class="label">Unpaid balance</div><div class="val">${escapeHtml(fmtUSD(c.unpaid?.total ?? 0))}</div></div>
    </div>

    <h2>${searchQuery ? `Matching commissions (${(rows || []).length})` : `Recent commissions (${(rows || []).length})`}</h2>
    ${(rows || []).length === 0 ? `<div class="sub">No matching rows.</div>` : `
    <table>
      <thead>
        <tr>
          <th>Earned</th>
          <th>Receiver</th>
          <th>Customer</th>
          <th>Type</th>
          <th style="text-align:right">Txn $</th>
          <th style="text-align:right">Commission</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>${commissionRowsHtml}</tbody>
      <tfoot>
        <tr>
          <td colspan="4" class="label">Total · ${rows.length} ${rows.length === 1 ? "row" : "rows"}</td>
          <td class="num">${escapeHtml(fmtUSD(txnTotal))}</td>
          <td class="num grand">${escapeHtml(fmtUSD(commTotal))}</td>
          <td></td>
        </tr>
      </tfoot>
    </table>`}

    <div class="footer">
      Zcardon · Generated from the Agent Portal · Confidential · Do not share outside the Zcardon platform.
    </div>
  </div>
  <script>
    if (!window.matchMedia('print').matches) {
      setTimeout(function () { window.print(); }, 250);
    }
  </script>
</body>
</html>`;

  // Print via a Blob URL. This is more reliable than `window.open("", "_blank")`
  // (which was returning null in modern browsers when the features string
  // included `noopener,noreferrer`) AND it avoids the ~2MB data-URL size cap
  // that bites on long commission tables.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (w) {
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  }
  // Popup truly blocked — emit a clickable fallback via an anchor element.
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 60_000);
  return { ok: true, fallback: true };
}
